import { CurrencySelector } from "@circle-react-shared/Billing/CurrencySelector";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Typography } from "@circle-react-uikit/Typography";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import {
  getNextTier,
  isPlanBasic,
  isPlanPlus,
} from "@circle-react/helpers/planHelpers";
import { Button } from "@circleco/compass/components/Button";
import { useMemo } from "react";

import { t } from "@/i18n-js/instance";
import { planSettingsPath } from "@/react/helpers/urlHelpers";

import { useUpgradePlanContext } from "./../UpgradePlanContext";
import { UpgradePlanModalBody } from "./../UpgradePlanModalBody";
import { UpgradePlanModalFooter } from "./../UpgradePlanModalFooter";
import { UpgradePlanModalHeader } from "./../UpgradePlanModalHeader";
import { getDisplayName, getDisplaySubheading } from "./helpers";
import { PlanFeaturesList } from "./PlanFeaturesList";
import { PlanLineItems } from "./PlanLineItems";

const i18nRoot = "pricing_plans.upgrade_modal";

interface UpgradePlanDetailsProps {
  hideModal: () => void;
  onSubmitHandler: () => void;
  subheading?: string;
}

const getEverythingInCopy = (
  isEmailHubOnlyUpgrade: boolean,
  nextTier: string | undefined,
  planTier: string | undefined,
): string => {
  if (isEmailHubOnlyUpgrade) {
    return "email_hub_features_title";
  }
  if (isPlanPlus(planTier)) {
    return "everything_in_plus";
  }
  if (nextTier === planTier) {
    return `everything_in_${planTier}`;
  }
  return "everything_in_your_plan_plus";
};

export const UpgradePlanDetails = ({
  subheading = "",
  hideModal,
  onSubmitHandler,
}: UpgradePlanDetailsProps) => {
  const {
    billingCurrency,
    setBillingCurrency,
    plan,
    emailHubPlan,
    shouldShowCurrencySelection,
    isEmailHubOnlyUpgrade,
    isDoubleUpgrade,
  } = useUpgradePlanContext();

  const { currentCommunity } = usePunditUserContext();
  const { current_plan_tier: currentPlanTier } = currentCommunity || {};

  const nextTier = currentPlanTier && getNextTier(currentPlanTier);
  const displayName = getDisplayName(plan, emailHubPlan);
  const everythingInCopy = getEverythingInCopy(
    isEmailHubOnlyUpgrade,
    nextTier || "",
    plan?.tier || "",
  );

  const shouldShowUpgradeFeatureList = !isPlanBasic(plan?.tier);

  const featuresToDisplay =
    plan?.main_features || emailHubPlan?.main_features || [];

  const displaySubheading = getDisplaySubheading(
    subheading,
    isEmailHubOnlyUpgrade,
  );
  const upgradeActionLabel = isEmailHubOnlyUpgrade
    ? t([i18nRoot, "actions.enable_email_hub"])
    : t([i18nRoot, "actions.upgrade"], { plan: displayName });
  const upgradeAction = useMemo(
    () => (
      <div className="w-full">
        <Button
          variant="gradient"
          size="lg"
          fullWidth
          onClick={onSubmitHandler}
        >
          {upgradeActionLabel}
        </Button>
      </div>
    ),
    [onSubmitHandler, upgradeActionLabel],
  );

  return (
    <>
      <UpgradePlanModalHeader
        title={
          isEmailHubOnlyUpgrade
            ? t([i18nRoot, "upgrade_title_email_hub_only"])
            : t([i18nRoot, "upgrade_title"], { plan: displayName })
        }
      >
        <Modal.Title>
          {isEmailHubOnlyUpgrade
            ? t([i18nRoot, "upgrade_title_email_hub_only"])
            : t([i18nRoot, "upgrade_title"], {
                plan: displayName,
              })}
        </Modal.Title>

        <Modal.HeaderActions>
          <Modal.CloseButton onClick={hideModal} />
        </Modal.HeaderActions>
      </UpgradePlanModalHeader>
      <UpgradePlanModalBody>
        {displaySubheading && (
          <div className="mb-6">
            <Typography.BodyMd color="text-default">
              {displaySubheading}
            </Typography.BodyMd>
          </div>
        )}

        <PlanLineItems />

        {shouldShowCurrencySelection && (
          <CurrencySelector
            billingCurrency={billingCurrency ?? null}
            onCurrencyChange={setBillingCurrency}
            className="mt-2 bg-tertiary"
          />
        )}

        {!isDoubleUpgrade && shouldShowUpgradeFeatureList && (
          <div className="mt-6">
            <Typography.LabelMd weight="semibold">
              {t([i18nRoot, everythingInCopy])}
            </Typography.LabelMd>
            <div className="mt-3">
              <PlanFeaturesList
                features={featuresToDisplay}
                iconType="16-star-fill"
                iconColor="text-blue-highlight"
              />
            </div>
            {!isEmailHubOnlyUpgrade && (
              <div className="mt-5">
                <a href={planSettingsPath()}>
                  <Typography.LabelMd weight="medium" color="text-default">
                    {t("pricing_plans.upgrade_modal.learn_more")}
                  </Typography.LabelMd>
                </a>
              </div>
            )}
          </div>
        )}
      </UpgradePlanModalBody>
      <UpgradePlanModalFooter className="mt-5" layout="full-width">
        {upgradeAction}
      </UpgradePlanModalFooter>
    </>
  );
};
